/**
 * ============================================================================
 * PAGE TABS — the app's page switcher (spec navigation-model R1).
 * ============================================================================
 *
 * A full-width strip under the product bar showing the ACTIVE dashboard's
 * pages (from its structure config), current page highlighted. This is THE
 * page navigation — there is no side nav rail — and the strip is the same
 * presentation in the standalone and embedded shells.
 *
 * Rendered as react-router `<Link>` (real anchors — shareable URLs,
 * middle-click / open-in-new-tab / copy-link honor a BrowserRouter basename,
 * Cmd/Ctrl/Shift click fall through natively), not a Cloudscape Tabs content
 * switcher: pages are routes, and the visual (underline strip) is
 * deliberately distinct from the in-page segmented switcher on the Detailed
 * View so the two levels never read as siblings.
 *
 * IDENTITY comes from the URL (not `useActiveDashboard`, which reads the
 * store): PageTabs mounts OUTSIDE DashboardGate, so on a cold/deep-link load
 * to a non-default dashboard the store hasn't caught up yet — deriving from
 * the URL keeps the strip in lockstep with the pathname and matches the
 * gate's own convention (parseDashboardSlug from :dashboardSlug).
 */

import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  dashboardStructure,
  parseDashboardSlug,
  toDashboardSlug,
  DEFAULT_DASHBOARD_SLUG,
  PageDef,
} from 'src/config/dashboards';
import {
  selectFeatureAccessible,
  useGetPermissionsQuery,
} from 'src/features/api/permissionsApi';
import { DashboardSwitcher } from 'src/components/layout/DashboardSwitcher';
import { WbrAgentLauncher } from 'src/components/chat/WbrAgentLauncher';

/**
 * Map any route (incl. parameterised children) to the page tab it belongs to.
 * Hrefs are slug-prefixed ("/kfe-wbr/detailed-view"), so match the sub-path
 * after the slug against the structure's page routes (longest prefix wins) and
 * re-prefix the winner. Derived from the structure so route renames need no
 * change here. The legacy /network/:id sub-path maps to the view page.
 */
export function activeHrefFor(
  pathname: string,
  slug: string,
  pages: PageDef[],
): string {
  const base = `/${slug}`;
  const sub = pathname.replace(base, '') || '/';
  const viewPage = pages.find((p) => p.kind === 'view');
  if (sub.startsWith('/network') && viewPage) return `${base}${viewPage.route}`;
  const match = pages
    .filter(
      (p) =>
        sub === p.route || (p.route !== '/' && sub.startsWith(`${p.route}/`)),
    )
    .sort((a, b) => b.route.length - a.route.length)[0];
  const route = match?.route ?? '/';
  return route === '/' ? base : `${base}${route}`;
}

export const PageTabs: React.FC = () => {
  const location = useLocation();
  // The router mount doesn't parameterise our slot, so pull the first path
  // segment from the URL and resolve it via the same helper the gate uses.
  const params = useParams<{ dashboardSlug?: string }>();
  const rawSlug =
    params.dashboardSlug ?? location.pathname.split('/').filter(Boolean)[0];
  const dashboardId = parseDashboardSlug(rawSlug);
  // Always the CANONICAL slug ("<id>-wbr") — parseDashboardSlug also accepts
  // a bare id ("fai"), so we regenerate through toDashboardSlug rather than
  // reusing the raw URL segment. An unknown/absent slug falls back to the
  // default dashboard's slug (same as App's catch-all redirect), so the
  // strip never renders empty and every tab href matches the app's URL form.
  const slug = dashboardId
    ? toDashboardSlug(dashboardId)
    : DEFAULT_DASHBOARD_SLUG;
  const structure = dashboardStructure(dashboardId ?? '');
  const home = `/${slug}`;
  // Normalise the pathname's first segment to the canonical slug too, or
  // activeHrefFor's `pathname.replace('/' + slug, '')` misses a bare-id URL
  // ("/fai/detailed-view" vs canonical "kfe-wbr") and every tab falls back
  // to the home highlight.
  const canonicalPathname = rawSlug
    ? location.pathname.replace(`/${rawSlug}`, `/${slug}`)
    : location.pathname;
  const activeHref = activeHrefFor(canonicalPathname, slug, structure.pages);
  // Feature-gated tabs (fail-closed, same rule as the pages themselves): a
  // page carrying a featureId renders its tab only once the caller's
  // GetPermissions profile confirms access.
  const { data: permissions } = useGetPermissionsQuery();
  const visiblePages = structure.pages.filter(
    (page) =>
      !page.featureId || selectFeatureAccessible(permissions, page.featureId),
  );

  return (
    <nav className="page-tabs" aria-label="Dashboard pages">
      <div className="page-tabs__pages">
        {visiblePages.map((page) => {
          const to = page.route === '/' ? home : `${home}${page.route}`;
          const active = to === activeHref;
          return (
            <Link
              key={page.route}
              to={to}
              className={`page-tabs__tab${
                active ? ' page-tabs__tab--active' : ''
              }`}
              aria-current={active ? 'page' : undefined}
            >
              {page.title}
            </Link>
          );
        })}
      </div>
      {/* Right corner: dashboard switcher (context) + WBR Agent launcher
          (accented action). The launcher carries its own leading divider so
          nothing dangles when it is hidden (no chat access). */}
      <div className="page-tabs__right">
        <DashboardSwitcher />
        <WbrAgentLauncher />
      </div>
    </nav>
  );
};
